import { store } from '../state/store';
import { computeWeekInfo, formatDisplayDate } from '../utils/date';
import { openWeekModal, WEEK_PRESETS } from './modal';

let tooltipEl: HTMLElement | null = null;

export function initGrid(): void {
  tooltipEl = document.getElementById('grid-tooltip');
}

export function renderGrid(): void {
  const container = document.getElementById('calendar-grid');
  if (!container) return;

  container.innerHTML = '';

  const totalYears = store.meta.total_years;
  const birthDate = store.getBirthDate();
  const currentWeek = store.currentWeekIndex;
  const eras = store.meta.eras;
  const highlightedEraId = store.highlightedEraId;

  const wrapper = document.createElement('div');
  wrapper.className = 'calendar-grid-container';

  // 1. Column header (Weeks 1 to 52)
  const colsHeader = document.createElement('div');
  colsHeader.className = 'grid-cols-header';
  for (let w = 1; w <= 52; w++) {
    const colNum = document.createElement('div');
    colNum.className = 'grid-col-num';
    if (w === 1 || w % 5 === 0 || w === 52) {
      colNum.textContent = String(w);
    }
    colsHeader.appendChild(colNum);
  }
  wrapper.appendChild(colsHeader);

  // 2. Rows: Year 0 to totalYears - 1
  for (let y = 0; y < totalYears; y++) {
    const row = document.createElement('div');
    row.className = 'grid-year-row';

    // Year Label
    const label = document.createElement('div');
    label.className = `grid-year-label ${y % 10 === 0 ? 'decade-marker' : ''}`;
    label.textContent = `${y}`;
    row.appendChild(label);

    // 52 Week boxes
    const weeksRow = document.createElement('div');
    weeksRow.className = 'grid-weeks-row';

    for (let w = 0; w < 52; w++) {
      const weekIndex = y * 52 + w;
      const weekInfo = computeWeekInfo(weekIndex, birthDate, currentWeek, eras, store.notes);

      const box = document.createElement('div');
      box.className = 'week-box';
      box.dataset.weekIndex = String(weekIndex);

      if (weekInfo.isCurrent) {
        box.classList.add('current');
      } else if (weekInfo.isPassed) {
        box.classList.add('passed');
      } else {
        box.classList.add('future');
      }

      if (weekInfo.note) {
        box.classList.add('has-note');
        if (weekInfo.note.milestone_tag) {
          box.classList.add('has-milestone');
        }
      }

      // Apply Custom Week Note Color or Era Coloring
      if (weekInfo.note?.color) {
        box.style.backgroundColor = weekInfo.note.color;
        box.style.borderColor = weekInfo.note.color;
      } else if (weekInfo.eras.length > 0) {
        const primaryEra = weekInfo.eras[0];
        box.style.backgroundColor = primaryEra.color;
        box.style.borderColor = primaryEra.color;

        // If era is private, use slightly dotted or distinct border if admin
        if (primaryEra.is_private) {
          box.style.opacity = '0.9';
        }
      }

      // If an era is selected/highlighted in legend
      if (highlightedEraId) {
        const matches = weekInfo.eras.some((e) => e.id === highlightedEraId);
        if (matches) {
          box.style.filter = 'brightness(1.25)';
          box.style.transform = 'scale(1.2)';
          box.style.zIndex = '5';
        } else {
          box.style.opacity = '0.25';
        }
      }

      // Event listeners for Tooltip and Click
      box.addEventListener('mouseenter', (e) => showTooltip(e, weekInfo));
      box.addEventListener('mouseleave', hideTooltip);
      box.addEventListener('click', () => openWeekModal(weekInfo));

      weeksRow.appendChild(box);
    }

    row.appendChild(weeksRow);
    wrapper.appendChild(row);
  }

  container.appendChild(wrapper);
}

function showTooltip(e: MouseEvent, info: ReturnType<typeof computeWeekInfo>): void {
  if (!tooltipEl) return;

  const startStr = formatDisplayDate(info.startDate);
  const endStr = formatDisplayDate(info.endDate);
  const eraNames = info.eras.map((e) => e.title).join(', ');

  let content = `
    <div class="tooltip-title">Age ${info.ageYears} • Week ${info.weekOfYear}</div>
    <div class="tooltip-meta">${startStr} – ${endStr}</div>
  `;

  if (eraNames) {
    content += `<div class="tooltip-era">📍 <span>${escapeHtml(eraNames)}</span></div>`;
  }

  if (info.note?.assessment || info.note?.color) {
    const matchedPreset = WEEK_PRESETS.find((p) => p.id === info.note?.assessment || (info.note?.color && p.color.toLowerCase() === info.note.color.toLowerCase()));
    if (matchedPreset) {
      content += `<div class="tooltip-note" style="color: ${matchedPreset.color}"><span>${matchedPreset.emoji} ${escapeHtml(matchedPreset.name)}</span></div>`;
    }
  }

  if (info.note?.title) {
    const icon = info.note.milestone_tag ? '🎯' : '📝';
    content += `<div class="tooltip-note">${icon} <strong>${escapeHtml(info.note.title)}</strong></div>`;
  } else if (info.note?.content) {
    content += `<div class="tooltip-note">📝 <span>${escapeHtml(info.note.content.substring(0, 45))}...</span></div>`;
  }

  tooltipEl.innerHTML = content;
  tooltipEl.classList.remove('hidden');

  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;

  // Viewport clamping so tooltip never overflows the screen horizontally
  const tooltipWidth = 200;
  const minX = tooltipWidth / 2 + 10;
  const maxX = window.innerWidth - tooltipWidth / 2 - 10;
  const clampedX = Math.max(minX, Math.min(maxX, centerX));

  tooltipEl.style.left = `${clampedX}px`;

  // If hovering near the top edge of the window, display tooltip below the box
  if (rect.top < 110) {
    tooltipEl.classList.add('tooltip-bottom');
    tooltipEl.style.top = `${rect.bottom}px`;
  } else {
    tooltipEl.classList.remove('tooltip-bottom');
    tooltipEl.style.top = `${rect.top}px`;
  }
}

function hideTooltip(): void {
  if (tooltipEl) {
    tooltipEl.classList.add('hidden');
  }
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
